// src/pages/DebtStudio/context/DebtStudioContext.jsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useRef,
} from "react";
import dayjs from "lib/dayjs";
import { supabase } from "services/supabaseClient";
import { useProfile } from "context/ProfileContext";
import { useDialog } from "context/DialogContext";
import useCurrentOwner from "components/FileManager/hooks/useCurrentOwner";
import { recomputeDerivedFields } from "pages/DebtStudio/components/Issues/DealStructure/hooks/useDebtScheduleLogic";
import { buildSourcesUses } from "pages/DebtStudio/lib/buildSourcesUses";
import { normalizeRefundingSolutionType } from "pages/DebtStudio/lib/refundingSolutionTypes";
import { normalizeDealPurpose } from "pages/DebtStudio/lib/combination";
import {
  getDebtStudioPermissions,
  normalizeSharing,
} from "lib/permissions";

/* ────────────────────────────────
   Additional Sources helpers
──────────────────────────────── */

function normalizeAdditionalSources(val) {
  if (!val) return [];
  if (Array.isArray(val)) {
    return val
      .map((x) => {
        if (!x || typeof x !== "object") return null;
        const amount = Number(x.amount);
        return {
          source: String(x.source ?? ""),
          description: String(x.description ?? ""),
          amount: Number.isFinite(amount) ? amount : 0,
        };
      })
      .filter(Boolean);
  }

  return [];
}

/** Strict compactor for persistence, removes blank rows. */
function compactAdditionalSources(val) {
  const rows = normalizeAdditionalSources(val);
  return rows.filter((x) => {
    const hasSource = !!String(x.source ?? "").trim();
    const hasAmount = Number(x.amount) !== 0;
    const hasDesc = !!String(x.description ?? "").trim();
    return hasSource || hasAmount || hasDesc;
  });
}

/* ────────────────────────────────
   Wrap helpers
   - wrap_config
   - wrap_manual_cashflows
   - wrap_ds_issue_refs
──────────────────────────────── */

function normalizeWrapManualCashflows(val) {
  if (!val || !Array.isArray(val)) return [];

  return val
    .map((x) => {
      if (!x || typeof x !== "object") return null;
      const total = Number(x.total);
      return {
        date: String(x.date ?? ""),
        total: Number.isFinite(total) ? total : 0,
      };
    })
    .filter(Boolean);
}

function compactWrapManualCashflows(val) {
  const rows = normalizeWrapManualCashflows(val);
  return rows.filter((x) => {
    const hasDate = !!String(x.date ?? "").trim();
    const hasTotal = Number(x.total) !== 0;
    return hasDate || hasTotal;
  });
}

function normalizeWrapDsIssueRefs(val) {
  if (!val || !Array.isArray(val)) return [];

  return val
    .map((x) => {
      if (!x || typeof x !== "object") return null;
      return {
        dsDealId: String(
          x.dsDealId ?? x.ds_deal_id ?? x.id ?? ""
        ).trim(),
        name: String(x.name ?? ""),
      };
    })
    .filter(Boolean);
}

function compactWrapDsIssueRefs(val) {
  const rows = normalizeWrapDsIssueRefs(val);
  return rows.filter((x) => {
    const hasId = !!String(x.dsDealId ?? "").trim();
    const hasName = !!String(x.name ?? "").trim();
    return hasId || hasName;
  });
}

function normalizeWrapConfig(val, manualCashflows = null, dsIssueRefs = null) {
  const raw =
    val && typeof val === "object" && !Array.isArray(val) ? val : {};

  const normalizedManual = normalizeWrapManualCashflows(
    manualCashflows ?? raw.wrapPaymentRows ?? []
  );

  const normalizedRefs = normalizeWrapDsIssueRefs(
    dsIssueRefs ?? raw.dsIssueRefs ?? raw.wrapDsIssueRefs ?? []
  );

  let sourceMode = String(raw.sourceMode || "").trim();
  if (!sourceMode) {
    const hasManual = normalizedManual.length > 0;
    const hasRefs = normalizedRefs.length > 0;
    sourceMode = hasManual && hasRefs ? "combined" : hasRefs ? "ds_issue" : "manual";
  }

  return {
    enabled:
      raw.enabled != null
        ? !!raw.enabled
        : normalizedManual.length > 0 || normalizedRefs.length > 0,
    sourceMode,
    inputMode: String(raw.inputMode || "dated_totals"),
    wrapPaymentRows: normalizedManual,
  };
}

function compactWrapConfig(val, manualCashflows = null, dsIssueRefs = null) {
  const cfg = normalizeWrapConfig(val, manualCashflows, dsIssueRefs);
  return {
    enabled: !!cfg.enabled,
    sourceMode: cfg.sourceMode || "manual",
    inputMode: cfg.inputMode || "dated_totals",
  };
}

/* ────────────────────────────────
   Refunded Issues helpers (ds_deals.refunded_issues)
   Manual entry only for now
──────────────────────────────── */

function normalizeRefundingManualDeposits(val) {
  if (!val) return [];
  if (!Array.isArray(val)) return [];

  return val
    .map((x) => {
      if (!x || typeof x !== "object") return null;

      const issueName = String(x.issueName ?? x.issue_name ?? "");
      const amount = Number(x.amount ?? x.deposit_amount);

      return {
        issueName,
        amount: Number.isFinite(amount) ? amount : 0,
      };
    })
    .filter(Boolean);
}

/** Strict compactor for persistence, removes blank rows. */
function compactRefundingManualDeposits(val) {
  const rows = normalizeRefundingManualDeposits(val);
  return rows.filter((x) => {
    const hasName = !!String(x.issueName ?? "").trim();
    const hasAmount = Number(x.amount) !== 0;
    return hasName || hasAmount;
  });
}

/**
 * Build ds_deals.refunded_issues (jsonb array) from current form state.
 * For now: manual entries only.
 */
function buildRefundedIssuesForDb(form) {
  const mode = String(form?.refundingMode ?? "").trim();
  if (mode === "ds_issue") {
    const entries = Array.isArray(form?.refunded_issues)
      ? form.refunded_issues
      : [];
    return entries.filter(
      (entry) => entry && entry.entry_type === "ds_issue" && entry.ds_deal_id
    ).map((entry) => ({
      ...entry,
      solution_type: normalizeRefundingSolutionType(form?.refundingSolutionType || entry.solution_type),
    }));
  }
  if (mode !== "manual") return [];

  const rows = compactRefundingManualDeposits(form?.refundingManualDeposits);
  return rows.map((r) => ({
    entry_type: "manual",
    issue_name: String(r.issueName ?? "").trim(),
    deposit_amount: Number(r.amount) || 0,
  }));
}

/**
 * Hydrate UI fields from ds_deals.refunded_issues (jsonb array).
 * For now: we only implement manual hydration.
 */
function hydrateRefundingUiFromRefundedIssues(refundedIssues) {
  if (!Array.isArray(refundedIssues) || refundedIssues.length === 0) {
    return {
      refundingMode: "manual",
      refundingManualDeposits: [],
    };
  }

  const manual = refundedIssues.filter(
    (e) => e && typeof e === "object" && String(e.entry_type) === "manual"
  );

  if (manual.length > 0) {
    return {
      refundingMode: "manual",
      refundingManualDeposits: manual.map((e) => ({
        issueName: String(e.issue_name ?? ""),
        amount: Number(e.deposit_amount) || 0,
      })),
    };
  }

  // Placeholder for ds_issue hydration later
  const hasDs = refundedIssues.some(
    (e) => e && typeof e === "object" && String(e.entry_type) === "ds_issue"
  );

  return {
    refundingMode: hasDs ? "ds_issue" : "manual",
    refundingManualDeposits: [],
  };
}

/* ────────────────────────────────
   Outputs helpers (ds_deals_outputs)
──────────────────────────────── */

function extractNetProceedsFromSourcesUses(sourcesUses) {
  const uses = Array.isArray(sourcesUses?.uses) ? sourcesUses.uses : [];
  const row = uses.find(
    (u) => String(u?.label || "").trim().toLowerCase() === "net proceeds"
  );
  const amt = Number(row?.amount);
  return Number.isFinite(amt) ? amt : null;
}

/* ────────────────────────────────
   Structure signature helpers
──────────────────────────────── */

function structureSignatureFromForm(form) {
  return {
    firstCouponDate: form?.firstCouponDate || "",
    firstPrincipalDate: form?.firstPrincipalDate || "",
    finalMaturityDate: form?.finalMaturityDate || "",
    principalFrequency: form?.principalFrequency || "",
    couponFrequency: form?.couponFrequency || "",
  };
}

function signaturesEqual(a, b) {
  try {
    return JSON.stringify(a || {}) === JSON.stringify(b || {});
  } catch {
    return false;
  }
}

/* ────────────────────────────────
   Default form
──────────────────────────────── */
const COMMON_DEFAULTS = {
  id: undefined,
  created_by: null,
  issuerId: null,
  issuerNameRaw: "",
  issuerStatus: "custom",
  dealName: "",
  financingType: "bond",
  dealType: "new_money",
  dealClass: "org",
  lienPriority: "senior",
  interestMode: "fixed",
  taxStatus: "tax_exempt",
  sharing: "user",
  status: "draft",
  datedDate: "",
  deliveryDate: "",
  firstCouponDate: "",
  firstPrincipalDate: "",
  finalMaturityDate: "",
  firstCallDate: "",
  firstCallDateToken: 0,
  fiscalYearEnd: "",
  parAmount: null,
  couponRate: 5,
  dayCount: "30/360",
  capiEnabled: false,
  capiDate: "",
  capiInvestmentRate: 0,
  scale: null,
  structureMode: "level_payment",
  amortizationPeriods: null,
  loanTermPeriods: null,
  targetType: "par_amount",
  targetValue: null,
  targetGross: null,
  fundingType: "gross",
  additionalSources: [],
  refundingMode: "manual",
  refundingSolutionType: "uniform",
  refundingManualDeposits: [],
  wrapConfig: {
    enabled: false,
    sourceMode: "manual",
    inputMode: "dated_totals",
    wrapPaymentRows: [],
  },
  wrapManualCashflows: [],
  wrapDsIssueRefs: [],
};

export const FINANCING_TYPE_TEMPLATES = {
  bond: {
    financingType: "bond",
    denomination: 5_000,
    couponFrequency: "semiannual",
    principalFrequency: "annual",
    dayCount: "30/360",
    pricingMode: "single",
  },

  loan: {
    financingType: "loan",
    denomination: 0.01,
    couponFrequency: "monthly",
    principalFrequency: "monthly",
    dayCount: "ACT/360",
    pricingMode: "single",
  },
};

export const DEFAULT_FORM = {
  ...COMMON_DEFAULTS,
  ...FINANCING_TYPE_TEMPLATES.bond,
};

export function applyFinancingTypeTemplate(prev, financingType) {
  const key = String(financingType || "bond").toLowerCase();

  if (String(prev?.financingType || "bond").toLowerCase() === key) {
    return prev;
  }

  const template =
    FINANCING_TYPE_TEMPLATES[key] || FINANCING_TYPE_TEMPLATES.bond;

  return {
    ...prev,
    financingType: template.financingType || key,
    denomination: template.denomination,
    couponFrequency: template.couponFrequency,
    principalFrequency: template.principalFrequency,
    dayCount: template.dayCount,
  };
}

const DebtStudioContext = createContext(null);

export function DebtStudioProvider({ children }) {
  const { profile, selectedOrg } = useProfile();
  const { alert } = useDialog();

  const [form, setForm] = useState(DEFAULT_FORM);
  const currentOwner = useCurrentOwner(form?.created_by);
  const [structureRows, setStructureRows] = useState([]);
  const [scale, setScale] = useState(null);
  const [solution, setSolution] = useState(null);
  const [adminLibraryEditEnabled, setAdminLibraryEditEnabled] = useState(false);

  const [lastSavedSnapshot, setLastSavedSnapshot] = useState(null);

  const [benchmarkCurveId, setBenchmarkCurveId] = useState("muni-aaa-go");
  const [interpMethod, setInterpMethod] = useState("linear");

  const [isDirty, _setIsDirty] = useState(false);
  const [suppressDirty, setSuppressDirty] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // signature of structure-driving terms (material)
  const [structureSignature, setStructureSignature] = useState(
    structureSignatureFromForm(DEFAULT_FORM)
  );

  // last First Call Date edit token applied to structureRows
  const [firstCallAppliedToken, setFirstCallAppliedToken] = useState(0);

  const hasRestoredOnce = useRef(false);
  const previousOrgIdRef = useRef(selectedOrg?.id ?? null);
  const workspaceResettingRef = useRef(false);

  const SESSION_KEY = "debtstudio.session";
  const BACKUP_KEY = "debtstudio.lastSessionBackup";
  const INACTIVITY_KEY = "compass.inactivityLogout";

  function setIsDirty(next) {
    _setIsDirty(next);
  }

  /* ────────────────────────────────
     Ownership + Lock authority (UI-first)
  ───────────────────────────────── */

  const permissions = useMemo(
    () => getDebtStudioPermissions(form, profile),
    [form, profile]
  );

  const { isOwner } = permissions;

  const isPlatformAdmin = !!profile?.is_admin;

  const isLibraryDeal = useMemo(() => {
    const dealClass = String(
      form?.dealClass ?? form?.deal_class ?? "org"
    ).toLowerCase();

    return dealClass === "library";
  }, [form?.dealClass, form?.deal_class]);

  const canEditLibraryDeal =
    isLibraryDeal && isPlatformAdmin && adminLibraryEditEnabled;

  const canEdit = isLibraryDeal ? canEditLibraryDeal : permissions.canEdit;
  const canDelete = isLibraryDeal
    ? canEditLibraryDeal
    : permissions.canDelete;
  const canChangeSharing = !isLibraryDeal && permissions.canChangeSharing;

  const isLocked = useMemo(() => {
    if (isLibraryDeal) return !canEditLibraryDeal;
    return permissions.isLocked;
  }, [isLibraryDeal, canEditLibraryDeal, permissions.isLocked]);

  // Back-compat: older callers use isReadOnly, keep semantics aligned with locking.
  const isReadOnly = isLocked;

  /* ────────────────────────────────
     Structure rebuild detection (material terms)
  ───────────────────────────────── */

  const currentSignature = useMemo(() => {
    return structureSignatureFromForm(form);
  }, [form]);

  const structureNeedsRebuild = useMemo(() => {
    const hasStructure =
      Array.isArray(structureRows) && structureRows.length > 0;
    if (!hasStructure) return false;
    return !signaturesEqual(structureSignature, currentSignature);
  }, [structureRows, structureSignature, currentSignature]);

  /* ────────────────────────────────
     Restore backup from localStorage (inactivity path)
  ───────────────────────────────── */
  useEffect(() => {
    const backup = localStorage.getItem(BACKUP_KEY);
    if (backup && !sessionStorage.getItem(SESSION_KEY)) {
      sessionStorage.setItem(SESSION_KEY, backup);
      localStorage.removeItem(BACKUP_KEY);
    }
  }, []);

  /* ────────────────────────────────
     Restore session on first mount
  ───────────────────────────────── */
  useEffect(() => {
    if (hasRestoredOnce.current) return;
    hasRestoredOnce.current = true;

    try {
      setSuppressDirty(true);

      const saved = sessionStorage.getItem(SESSION_KEY);
      if (!saved) {
        _setIsDirty(false);
        return;
      }

      const parsed = JSON.parse(saved);
      const savedOrgId = parsed.orgId ?? parsed.selectedOrg?.id ?? null;
      const currentOrgId = selectedOrg?.id ?? null;

      if (!savedOrgId || !currentOrgId || String(savedOrgId) !== String(currentOrgId)) {
        sessionStorage.removeItem(SESSION_KEY);
        _setIsDirty(false);
        return;
      }

      if (parsed.form) {
        const restored = {
          ...DEFAULT_FORM,
          ...parsed.form,
          fiscalYearEnd: parsed.form.fiscalYearEnd || "",
          firstCallDate: parsed.form.firstCallDate || "",
          firstCallDateToken: Number(parsed.form.firstCallDateToken || 0),

          status: parsed.form.status || DEFAULT_FORM.status,

          financingType:
            parsed.form.financingType ??
            parsed.form.financing_type ??
            DEFAULT_FORM.financingType,

          dealClass:
            parsed.form.dealClass ??
            parsed.form.deal_class ??
            DEFAULT_FORM.dealClass,

          lienPriority:
            parsed.form.lienPriority ??
            parsed.form.lien_priority ??
            DEFAULT_FORM.lienPriority,

          sharing: normalizeSharing(parsed.form.sharing),
        };

        restored.additionalSources = normalizeAdditionalSources(
          parsed.form.additionalSources
        );

        restored.refundingMode = parsed.form.refundingMode || "manual";
        restored.refundingManualDeposits = normalizeRefundingManualDeposits(
          parsed.form.refundingManualDeposits
        );

        restored.wrapManualCashflows = normalizeWrapManualCashflows(
          parsed.form.wrapManualCashflows ??
            parsed.form.wrap_manual_cashflows ??
            []
        );

        restored.wrapDsIssueRefs = normalizeWrapDsIssueRefs(
          parsed.form.wrapDsIssueRefs ??
            parsed.form.wrap_ds_issue_refs ??
            []
        );

        restored.wrapConfig = normalizeWrapConfig(
          parsed.form.wrapConfig ?? parsed.form.wrap_config,
          restored.wrapManualCashflows,
          restored.wrapDsIssueRefs
        );

        restored.capiEnabled = !!(
          parsed.form.capiEnabled ?? parsed.form.capi_enabled ?? false
        );

        if (!restored.capiEnabled) {
          restored.capiDate = "";
          restored.capiInvestmentRate = 0;
        } else {
          restored.capiDate =
            (parsed.form.capiDate ?? parsed.form.capi_date ?? "") || "";
          const r =
            parsed.form.capiInvestmentRate ??
            parsed.form.capi_investment_rate ??
            0;
          restored.capiInvestmentRate = r == null || r === "" ? 0 : Number(r);
        }

        // normalize nullability for target fields
        restored.targetValue =
          restored.targetValue == null || String(restored.targetValue).trim() === ""
            ? null
            : Number(restored.targetValue);
        restored.targetGross =
          restored.targetGross == null || String(restored.targetGross).trim() === ""
            ? null
            : Number(restored.targetGross);
        restored.fundingType = (restored.fundingType ?? "").trim() || "gross";

        setForm(restored);
      } else {
        setForm({ ...DEFAULT_FORM });
      }

      if (Array.isArray(parsed.structureRows)) setStructureRows(parsed.structureRows);
      if (parsed.scale !== undefined) setScale(parsed.scale);
      if (parsed.solution !== undefined) setSolution(parsed.solution);
      if (parsed.lastSavedSnapshot !== undefined)
        setLastSavedSnapshot(parsed.lastSavedSnapshot);
      if (parsed.benchmarkCurveId) setBenchmarkCurveId(parsed.benchmarkCurveId);
      if (parsed.interpMethod) setInterpMethod(parsed.interpMethod);

      if (parsed.structureSignature) {
        setStructureSignature(parsed.structureSignature);
      } else {
        setStructureSignature(structureSignatureFromForm(parsed.form || DEFAULT_FORM));
      }

      if (parsed.firstCallAppliedToken !== undefined) {
        setFirstCallAppliedToken(Number(parsed.firstCallAppliedToken || 0));
      } else {
        setFirstCallAppliedToken(Number(parsed?.form?.firstCallDateToken || 0));
      }

      localStorage.removeItem(INACTIVITY_KEY);
      localStorage.removeItem(BACKUP_KEY);

      _setIsDirty(false);
    } catch {
      _setIsDirty(false);
    } finally {
      setTimeout(() => setSuppressDirty(false), 0);
    }
  }, [selectedOrg]);

  /* Organization selection is a hard workspace boundary while mounted. */
  useEffect(() => {
    const nextOrgId = selectedOrg?.id ?? null;
    const previousOrgId = previousOrgIdRef.current;
    previousOrgIdRef.current = nextOrgId;

    if (previousOrgId == null || String(previousOrgId) === String(nextOrgId)) return;
    resetAll();
  }, [selectedOrg?.id]);

  /* ────────────────────────────────
     Persist DS session on changes
  ───────────────────────────────── */
  useEffect(() => {
    if (workspaceResettingRef.current || isLoggingOut || suppressDirty) return;

    const hasWorkingDeal = !!(selectedOrg && (form?.id || form?.dealName));
    if (!hasWorkingDeal) return;

    const payload = {
      orgId: selectedOrg.id,
      form: {
        ...form,
        additionalSources: normalizeAdditionalSources(form.additionalSources),
        refundingMode: form.refundingMode || "manual",
        refundingManualDeposits: normalizeRefundingManualDeposits(
          form.refundingManualDeposits
        ),
        wrapManualCashflows: normalizeWrapManualCashflows(
          form.wrapManualCashflows
        ),
        wrapDsIssueRefs: normalizeWrapDsIssueRefs(form.wrapDsIssueRefs),
        wrapConfig: normalizeWrapConfig(
          form.wrapConfig,
          form.wrapManualCashflows,
          form.wrapDsIssueRefs
        ),
      },
      structureRows,
      scale,
      solution,
      selectedOrg,
      lastSavedSnapshot,
      benchmarkCurveId,
      interpMethod,
      structureSignature,
      firstCallAppliedToken,
    };

    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
    } catch {
      // no-op
    }
  }, [
    form,
    structureRows,
    scale,
    solution,
    selectedOrg,
    lastSavedSnapshot,
    benchmarkCurveId,
    interpMethod,
    structureSignature,
    firstCallAppliedToken,
    isLoggingOut,
    suppressDirty,
  ]);

  /* ────────────────────────────────
     Active deal detection
  ───────────────────────────────── */
  const hasActiveDeal = useMemo(() => {
    const hasWorkingDeal = !!(
      selectedOrg && (form?.id || String(form?.dealName || "").trim())
    );
    return hasWorkingDeal;
  }, [selectedOrg, form?.id, form?.dealName]);

  /* ────────────────────────────────
     Silent update helper (no dirty tracking)
  ───────────────────────────────── */
  function runSilent(fn) {
    setSuppressDirty(true);
    try {
      fn?.();
    } finally {
      setTimeout(() => setSuppressDirty(false), 0);
    }
  }

  /* ────────────────────────────────
     Wrapped setters with dirty tracking
  ───────────────────────────────── */
  function updateForm(next) {
    if (isLocked) return;
    setForm((prev) => {
      const updatedRaw = typeof next === "function" ? next(prev) : next;

      const nextCapiEnabled = !!(
        updatedRaw?.capiEnabled ??
        updatedRaw?.capi_enabled ??
        false
      );

      const nextCapiDate = nextCapiEnabled
        ? (updatedRaw?.capiDate ?? updatedRaw?.capi_date ?? "") || ""
        : "";

      const rawRate =
        updatedRaw?.capiInvestmentRate ?? updatedRaw?.capi_investment_rate ?? 0;

      const nextCapiInvestmentRate = !nextCapiEnabled
        ? 0
        : rawRate == null || rawRate === ""
        ? 0
        : Number(rawRate);

      const directWrapManual = compactWrapManualCashflows(
        updatedRaw?.wrapManualCashflows ?? updatedRaw?.wrap_manual_cashflows
      );

      const configWrapManual = compactWrapManualCashflows(
        updatedRaw?.wrapConfig?.wrapPaymentRows
      );

      const prevWrapManual = compactWrapManualCashflows(prev?.wrapManualCashflows);

      const nextWrapManualCashflows =
        directWrapManual.length > 0
          ? directWrapManual
          : configWrapManual.length > 0
          ? configWrapManual
          : prevWrapManual;

      const nextWrapDsIssueRefs = normalizeWrapDsIssueRefs(
        updatedRaw?.wrapDsIssueRefs ??
          updatedRaw?.wrap_ds_issue_refs ??
          prev?.wrapDsIssueRefs
      );

      const nextWrapConfig = normalizeWrapConfig(
        updatedRaw?.wrapConfig ?? updatedRaw?.wrap_config,
        nextWrapManualCashflows,
        nextWrapDsIssueRefs
      );

      const updated = {
        ...updatedRaw,
        additionalSources: normalizeAdditionalSources(
          updatedRaw?.additionalSources
        ),
        refundingMode: updatedRaw?.refundingMode || "manual",
        refundingManualDeposits: normalizeRefundingManualDeposits(
          updatedRaw?.refundingManualDeposits
        ),
        wrapManualCashflows: nextWrapManualCashflows,
        wrapDsIssueRefs: nextWrapDsIssueRefs,
        wrapConfig: nextWrapConfig,
        firstCallDateToken: Number(updatedRaw?.firstCallDateToken || 0),
        status: updatedRaw?.status || DEFAULT_FORM.status,

        financingType:
          updatedRaw?.financingType ??
          updatedRaw?.financing_type ??
          DEFAULT_FORM.financingType,

        dealClass:
          updatedRaw?.dealClass ??
          updatedRaw?.deal_class ??
          DEFAULT_FORM.dealClass,

        capiEnabled: nextCapiEnabled,
        capiDate: nextCapiDate,
        capiInvestmentRate: nextCapiInvestmentRate,

        lienPriority:
          updatedRaw?.lienPriority ??
          updatedRaw?.lien_priority ??
          DEFAULT_FORM.lienPriority,

        amortizationPeriods:
          updatedRaw?.amortizationPeriods == null ||
          String(updatedRaw?.amortizationPeriods).trim() === ""
            ? null
            : Number(updatedRaw.amortizationPeriods),

        loanTermPeriods:
          updatedRaw?.loanTermPeriods == null ||
          String(updatedRaw?.loanTermPeriods).trim() === ""
            ? null
            : Number(updatedRaw.loanTermPeriods),

        targetValue:
          updatedRaw?.targetValue == null ||
          String(updatedRaw?.targetValue).trim() === ""
            ? null
            : Number(updatedRaw.targetValue),

        targetGross:
          updatedRaw?.targetGross == null ||
          String(updatedRaw?.targetGross).trim() === ""
            ? null
            : Number(updatedRaw.targetGross),

        fundingType: (updatedRaw?.fundingType ?? "").trim() || "gross",
        sharing: normalizeSharing(updatedRaw?.sharing),
      };

      if (!suppressDirty && JSON.stringify(updated) !== JSON.stringify(prev)) {
        setIsDirty(true);
      }
      return updated;
    });
  }

  function resolveWrapManualCashflows(formLike) {
    const direct = compactWrapManualCashflows(formLike?.wrapManualCashflows);
    if (direct.length > 0) return direct;

    const fromConfig = compactWrapManualCashflows(
      formLike?.wrapConfig?.wrapPaymentRows
    );
    if (fromConfig.length > 0) return fromConfig;

    return [];
  }

  function updateStructureRows(next) {
    if (isLocked) return;
    setStructureRows((prev) => {
      const updated = typeof next === "function" ? next(prev) : next;
      if (!suppressDirty && JSON.stringify(updated) !== JSON.stringify(prev)) {
        _setIsDirty(true);
        setSolution(null);
      }
      return updated;
    });
  }

  function updateScale(next) {
    if (isLocked) return;
    setScale((prev) => {
      const updated = typeof next === "function" ? next(prev) : next;
      if (!suppressDirty && JSON.stringify(updated) !== JSON.stringify(prev)) {
        _setIsDirty(true);
        setSolution(null);
      }
      return updated;
    });
  }

  /* ────────────────────────────────
     Structure helpers
  ───────────────────────────────── */

  function markStructureSignatureCurrent(nextForm = form) {
    setStructureSignature(structureSignatureFromForm(nextForm));
  }

  function applyFirstCallDate() {
    if (isLocked) return false;
    if (!Array.isArray(structureRows) || structureRows.length === 0) return false;

    const token = Number(form?.firstCallDateToken || 0);
    if (token === Number(firstCallAppliedToken || 0)) {
      return false;
    }

    const firstCall = form?.firstCallDate ? dayjs(form.firstCallDate) : null;
    const hasValidFirstCall = !!(firstCall && firstCall.isValid());
    const firstCallISO = hasValidFirstCall ? firstCall.format("YYYY-MM-DD") : null;

    let changed = false;

    const updated = structureRows.map((r) => {
      const rowDate = r?.date ? dayjs(r.date) : null;
      if (!rowDate || !rowDate.isValid()) return r;

      if (!hasValidFirstCall) {
        if (r.call_date != null) changed = true;
        return { ...r, call_date: null };
      }

      const nextCall = rowDate.isAfter(firstCall) ? firstCallISO : null;

      if ((r.call_date ?? null) !== (nextCall ?? null)) {
        changed = true;
      }

      return { ...r, call_date: nextCall };
    });

    setFirstCallAppliedToken(token);

    if (changed) {
      setStructureRows(updated);
      setSolution(null);
      _setIsDirty(true);
    }

    return changed;
  }

  function rebuildStructureFromTerms() {
    if (isLocked) return false;

    const rebuilt = buildOrUpdateStructure(form, []);
    if (!Array.isArray(rebuilt) || rebuilt.length === 0) return false;

    const isLoan =
      String(form?.financingType || "").trim().toLowerCase() === "loan";

    const loanTermPeriods = isLoan ? rebuilt.length : form?.loanTermPeriods;

    setStructureRows(rebuilt);
    setScale(null);
    setSolution(null);

    if (isLoan) {
      setForm((f) => ({
        ...f,
        loanTermPeriods,
        amortizationPeriods: loanTermPeriods,
        amortizationType: f?.amortizationType || "level_debt_service",
      }));
    }

    markStructureSignatureCurrent(form);

    setFirstCallAppliedToken(Number(form?.firstCallDateToken || 0));

    _setIsDirty(true);
    return true;
  }

  /* ────────────────────────────────
     Reset everything
  ───────────────────────────────── */
  function resetAll() {
    workspaceResettingRef.current = true;
    setIsLoggingOut(true);

    setSuppressDirty(true);
    try {
      setForm({ ...DEFAULT_FORM });
      setStructureRows([]);
      setScale(null);
      setSolution(null);
      setAdminLibraryEditEnabled(false);
      setLastSavedSnapshot(null);
      setBenchmarkCurveId("muni-aaa-go");
      setInterpMethod("linear");
      setStructureSignature(structureSignatureFromForm(DEFAULT_FORM));
      setFirstCallAppliedToken(0);
      _setIsDirty(false);

      if (localStorage.getItem(INACTIVITY_KEY)) {
        localStorage.removeItem(INACTIVITY_KEY);
      } else {
        sessionStorage.removeItem(SESSION_KEY);
        localStorage.removeItem(BACKUP_KEY);
      }
    } finally {
      setTimeout(() => {
        setSuppressDirty(false);
        setIsLoggingOut(false);
        workspaceResettingRef.current = false;
      }, 0);
    }
  }

  const resetForm = resetAll;

  /* ────────────────────────────────
     Ensure structure valid
  ───────────────────────────────── */
  const ensureStructure = () => {
    const { ok } = validateDealForm(form);
    if (!ok) return false;

    if (!structureRows || structureRows.length === 0) {
      const skeleton = buildOrUpdateStructure(form);
      if (skeleton.length > 0) {
        setStructureRows(skeleton);
        markStructureSignatureCurrent(form);

        setFirstCallAppliedToken(Number(form?.firstCallDateToken || 0));

        _setIsDirty(true);
        setSolution(null);
        return true;
      }

      void alert("Could not generate structure. Please verify deal dates.", {
        title: "Structure error",
        okLabel: "OK",
      });

      return false;
    }

    return true;
  };

  /* ────────────────────────────────
     Save deal
  ───────────────────────────────── */
  async function saveDeal({
    formOverride = null,
    structureRowsOverride = null,
    scaleOverride = null,
    selectedOrgOverride = null,
    forceInsert = false,
  } = {}) {
    const f = formOverride ?? form;
    const rows = structureRowsOverride ?? structureRows;
    const sc = scaleOverride ?? scale;
    const org = selectedOrgOverride ?? selectedOrg;

    if (
      selectedOrgOverride?.id &&
      selectedOrg?.id &&
      String(selectedOrgOverride.id) !== String(selectedOrg.id)
    ) {
      return { ok: false, reason: "organization_mismatch" };
    }

    const effectiveDealClass = String(
      f?.dealClass ?? f?.deal_class ?? "org"
    ).toLowerCase();
    const effectiveIsLibrary = effectiveDealClass === "library";
    const effectiveIsPlatformAdmin = !!profile?.is_admin;
    const effectiveCanEditLibraryDeal =
      effectiveIsLibrary &&
      effectiveIsPlatformAdmin &&
      (adminLibraryEditEnabled || forceInsert);

    if (effectiveIsLibrary && !effectiveCanEditLibraryDeal) {
      await alert("Library issues are read-only and cannot be saved.", {
        title: "Read-only",
        okLabel: "OK",
      });
      return { ok: false, reason: "library_locked" };
    }

    const effectivePermissions = getDebtStudioPermissions(f, profile);
    const effectiveIsReadOnly =
      effectivePermissions.isReadOnly && !effectiveCanEditLibraryDeal;

    if (effectiveIsReadOnly && !forceInsert) {
      await alert(
        "This deal is read-only. Use Save As to create your own editable copy.",
        { title: "Read-only", okLabel: "OK" }
      );
      return { ok: false, reason: "read_only" };
    }

    if (!org || !f?.dealName) {
      return { ok: false, reason: "missing_required_state" };
    }

    const formOrgId = f?.orgId ?? f?.org_id ?? null;
    if (
      !effectiveIsLibrary &&
      formOrgId &&
      String(formOrgId) !== String(org.id)
    ) {
      return { ok: false, reason: "organization_mismatch" };
    }

    if (localStorage.getItem(INACTIVITY_KEY)) {
      _setIsDirty(false);
      return { ok: false, reason: "inactivity_guard" };
    }

    try {
      const res = await supabase.auth.getSession();
      const session = res?.data?.session;

      if (!session) {
        _setIsDirty(false);
        return { ok: false, reason: "no_session" };
      }
    } catch (e) {
      console.error("❌ [saveDeal] auth.getSession failed", e);
      _setIsDirty(false);
      return { ok: false, reason: "auth_failed", error: e };
    }

    try {
      const normalizedAdditionalSources = compactAdditionalSources(
        f.additionalSources
      );

      const refundedIssuesForDb = buildRefundedIssuesForDb(f);
      
      const wrapManualCashflowsForDb = resolveWrapManualCashflows(f);

      const wrapDsIssueRefsForDb = compactWrapDsIssueRefs(
        f.wrapDsIssueRefs
      );

      const wrapConfigForDb = compactWrapConfig(
        f.wrapConfig,
        wrapManualCashflowsForDb,
        wrapDsIssueRefsForDb
      );

      const isCanonicalIssuer = !!f.issuerId;
      const normalizedSharing = forceInsert || effectivePermissions.canChangeSharing
        ? normalizeSharing(f.sharing)
        : normalizeSharing(lastSavedSnapshot?.form?.sharing ?? f.sharing);

      const issuerPayload = isCanonicalIssuer
        ? {
            issuer_id: f.issuerId,
            issuer_name_raw: f.issuerNameRaw?.trim() || null,
            issuer_status: "canonical",
          }
        : {
            issuer_id: null,
            issuer_name_raw: f.issuerNameRaw?.trim() || null,
            issuer_status: "custom",
          };

      const dbObj = {
        org_id: effectiveIsLibrary ? null : org.id,
          tenant_id: effectiveIsLibrary
            ? null
            : org?.tenant_id ?? profile?.tenant_id ?? null,

        ...issuerPayload,

        name: f.dealName,
        deal_type: normalizeDealPurpose(f.dealType),
        financing_type: f.financingType || "bond",

        deal_class: effectiveDealClass,

        lien_priority:
          f?.lienPriority == null || String(f.lienPriority).trim() === ""
            ? null
            : String(f.lienPriority).trim(),

        interest_mode: f.interestMode,
        tax_status: f.taxStatus,
        sharing: normalizedSharing,

        status: f.status || DEFAULT_FORM.status,

        dated_date: f.datedDate || null,
        delivery_date: f.deliveryDate || null,
        first_coupon_date: f.firstCouponDate || null,
        first_principal_date: f.firstPrincipalDate || null,
        final_maturity_date: f.finalMaturityDate || null,
        first_call_date: f.firstCallDate || null,
        fiscal_year_end: f.fiscalYearEnd || null,

        par_amount: f.parAmount,
        denomination: f.denomination,
        coupon_rate: f.couponRate,
        day_count: f.dayCount,
        coupon_frequency: f.couponFrequency,
        principal_frequency: f.principalFrequency,

        capi_enabled: !!f.capiEnabled,
        capi_date: f.capiEnabled ? (f.capiDate || null) : null,
        capi_investment_rate:
          f.capiEnabled &&
          f.capiInvestmentRate != null &&
          f.capiInvestmentRate !== ""
            ? Number(f.capiInvestmentRate)
            : null,

        pricing_mode: f.pricingMode,
        structure_mode: f.structureMode,
        amortization_periods:
          f.amortizationPeriods == null
            ? null
            : Number(f.amortizationPeriods),
        loan_term_periods:
          f.loanTermPeriods == null
            ? null
            : Number(f.loanTermPeriods),
        target_type: f.targetType,
        target_value:
          f.targetValue == null || String(f.targetValue).trim() === ""
            ? null
            : Number(f.targetValue),
        target_gross:
          f.targetGross == null || String(f.targetGross).trim() === ""
            ? null
            : Number(f.targetGross),

        funding_type: (f.fundingType ?? "").trim() || null,

        additional_sources:
          normalizedAdditionalSources.length > 0
            ? normalizedAdditionalSources
            : null,

        refunded_issues:
          refundedIssuesForDb.length > 0 ? refundedIssuesForDb : null,

        wrap_config:
          wrapConfigForDb.enabled ||
          wrapManualCashflowsForDb.length > 0 ||
          wrapDsIssueRefsForDb.length > 0
            ? wrapConfigForDb
            : null,

        wrap_manual_cashflows:
          wrapManualCashflowsForDb.length > 0 ? wrapManualCashflowsForDb : null,

        wrap_ds_issue_refs:
          wrapDsIssueRefsForDb.length > 0 ? wrapDsIssueRefsForDb : null,

        scale: sc ? JSON.stringify(sc) : null,
        structure: JSON.stringify(rows || []),
        cost_of_issuance: f.costOfIssuance
          ? JSON.stringify(f.costOfIssuance)
          : null,

        updated_at: new Date().toISOString(),
      };

      const shouldUpdate = !!(f.id && !forceInsert);

      let result;
      if (shouldUpdate) {
        let updateQuery = supabase
          .from("ds_deals")
          .update(dbObj)
          .eq("id", f.id);
        updateQuery = effectiveIsLibrary
          ? updateQuery.is("org_id", null)
          : updateQuery.eq("org_id", org.id);
        result = await updateQuery.select()
          .maybeSingle();
      } else {
        dbObj.created_by = profile.id;
        dbObj.created_at = new Date().toISOString();
        result = await supabase.from("ds_deals").insert([dbObj]).select().single();
      }

      const { data, error } = result;
      if (error) throw error;
      if (shouldUpdate && !data) {
        throw new Error(
          "The issue was not found in the selected organization or is no longer available."
        );
      }

      const savedWrapManualCashflows = normalizeWrapManualCashflows(
        data?.wrap_manual_cashflows ?? wrapManualCashflowsForDb
      );
      const savedWrapDsIssueRefs = normalizeWrapDsIssueRefs(
        data?.wrap_ds_issue_refs ?? wrapDsIssueRefsForDb
      );
      const savedWrapConfig = normalizeWrapConfig(
        data?.wrap_config ?? wrapConfigForDb,
        savedWrapManualCashflows,
        savedWrapDsIssueRefs
      );

      const savedForm = {
        ...f,
        id: data?.id ?? f?.id ?? null,
        orgId: data?.org_id ?? (effectiveIsLibrary ? null : org.id),
        created_by: data?.created_by ?? f?.created_by ?? profile?.id ?? null,

        financingType:
          data?.financing_type ??
          f?.financingType ??
          DEFAULT_FORM.financingType,

        dealClass: data?.deal_class ?? f?.dealClass ?? DEFAULT_FORM.dealClass,

        lienPriority:
          data?.lien_priority ??
          f?.lienPriority ??
          DEFAULT_FORM.lienPriority,

        amortizationPeriods:
          data?.amortization_periods ?? f?.amortizationPeriods ?? null,

        loanTermPeriods:
          data?.loan_term_periods ?? f?.loanTermPeriods ?? null,

        wrapManualCashflows: savedWrapManualCashflows,
        wrapDsIssueRefs: savedWrapDsIssueRefs,
        wrapConfig: savedWrapConfig,
        sharing: normalizeSharing(data?.sharing ?? normalizedSharing),
      };

      try {
        const dealId = savedForm.id;
        if (dealId) {
          const costOfIssuance =
            savedForm.costOfIssuance ??
            savedForm.cost_of_issuance ??
            f.costOfIssuance ??
            f.cost_of_issuance ??
            [];

          const normalizedForReportMath = {
            ...savedForm,
            additionalSources: normalizedAdditionalSources,
            costOfIssuance,
          };

          const sourcesUses = buildSourcesUses(normalizedForReportMath, rows);
          const netProceeds = extractNetProceedsFromSourcesUses(sourcesUses);

          if (Number.isFinite(netProceeds)) {
            const cashDate =
              savedForm?.deliveryDate ||
              savedForm?.datedDate ||
              dayjs().format("YYYY-MM-DD");

            const outRows = [
              {
                row_order: 1,
                cash_date: cashDate,
                bucket: "net_proceeds",
                amount: Math.round(netProceeds * 100) / 100,
                meta: { basis: "buildSourcesUses" },
              },
            ];

            const { error: outErr } = await supabase.rpc(
              "replace_ds_deal_output_rows",
              {
                p_ds_deal_id: dealId,
                p_output_type: "net_proceeds",
                p_rows: outRows,
              }
            );

            if (outErr) {
              const message = String(outErr.message || "");

              if (message.includes("Not authorized to update outputs")) {
                console.warn(
                  "Deal saved, but Net Proceeds output publish was skipped:",
                  outErr
                );
              } else {
                await alert(
                  "Deal saved, but Net Proceeds could not be published for FP Structurer linkage. " +
                    message,
                  { title: "Publish warning", okLabel: "OK" }
                );
              }
            }
          }
        }
      } catch (e) {
        console.error("❌ [saveDeal] net_proceeds output publish exception:", e);

        await alert(
          "Deal saved, but Net Proceeds could not be published for FP Structurer linkage.",
          { title: "Publish warning", okLabel: "OK" }
        );
      }

      setForm((prev) => ({ ...prev, ...savedForm }));

      setLastSavedSnapshot({
        form: savedForm,
        structureRows: rows,
        scale: sc,
      });

      _setIsDirty(false);
      localStorage.removeItem(BACKUP_KEY);

      return { ok: true, id: savedForm.id, data };
    } catch (err) {
      console.error("❌ [saveDeal] Error saving deal:", err);

      await alert("Error saving deal: " + err.message, {
        title: "Save failed",
        okLabel: "OK",
      });

      return { ok: false, reason: "db_error", error: err };
    }
  }

  /* ────────────────────────────────
     Revert to last saved
  ───────────────────────────────── */
  function revertToLastSaved() {
    if (!lastSavedSnapshot) {
      resetAll();
      return;
    }

    const { form: savedForm, structureRows: savedRows, scale: savedScale } =
      lastSavedSnapshot;

    setSuppressDirty(true);
    try {
      const next = {
        ...DEFAULT_FORM,
        ...savedForm,
        fiscalYearEnd: savedForm?.fiscalYearEnd || "",
        firstCallDate: savedForm?.firstCallDate || "",
        firstCallDateToken: Number(savedForm?.firstCallDateToken || 0),
        status: savedForm?.status || DEFAULT_FORM.status,

        financingType:
          savedForm?.financingType ??
          savedForm?.financing_type ??
          DEFAULT_FORM.financingType,

        dealClass:
          savedForm?.dealClass ??
          savedForm?.deal_class ??
          DEFAULT_FORM.dealClass,

        lienPriority:
          savedForm?.lienPriority ??
          savedForm?.lien_priority ??
          DEFAULT_FORM.lienPriority,

        sharing: normalizeSharing(savedForm?.sharing),
      };

      next.additionalSources = normalizeAdditionalSources(
        savedForm?.additionalSources
      );

      next.refundingMode = savedForm?.refundingMode || "manual";
      next.refundingManualDeposits = normalizeRefundingManualDeposits(
        savedForm?.refundingManualDeposits
      );

      next.wrapManualCashflows = normalizeWrapManualCashflows(
        savedForm?.wrapManualCashflows ??
          savedForm?.wrap_manual_cashflows ??
          savedForm?.wrapConfig?.wrapPaymentRows
      );

      next.wrapDsIssueRefs = normalizeWrapDsIssueRefs(
        savedForm?.wrapDsIssueRefs ?? savedForm?.wrap_ds_issue_refs
      );

      next.wrapConfig = normalizeWrapConfig(
        savedForm?.wrapConfig ?? savedForm?.wrap_config,
        next.wrapManualCashflows,
        next.wrapDsIssueRefs
      );

      next.capiEnabled = !!(next.capiEnabled ?? next.capi_enabled);

      if (!next.capiEnabled) {
        next.capiDate = "";
        next.capiInvestmentRate = 0;
      } else {
        next.capiDate = (next.capiDate ?? next.capi_date ?? "") || "";
        const r = next.capiInvestmentRate ?? next.capi_investment_rate ?? 0;
        next.capiInvestmentRate = r == null || r === "" ? 0 : Number(r);
      }

      next.targetValue =
        next.targetValue == null || String(next.targetValue).trim() === ""
          ? null
          : Number(next.targetValue);

      next.targetGross =
        next.targetGross == null || String(next.targetGross).trim() === ""
          ? null
          : Number(next.targetGross);

      next.fundingType = (next.fundingType ?? "").trim() || "gross";

      setForm(next);
      setStructureRows(savedRows || []);
      setScale(savedScale || null);
      setSolution(null);
      setStructureSignature(structureSignatureFromForm(next));
      setFirstCallAppliedToken(Number(next.firstCallDateToken || 0));
      _setIsDirty(false);
    } finally {
      setTimeout(() => setSuppressDirty(false), 0);
    }
  }

  /* ────────────────────────────────
     Load deal
  ───────────────────────────────── */
  function loadDeal({
    form: nextForm,
    structureRows: nextRows,
    scale: nextScale,
    solution: nextSolution,
    selectedOrg: requestedOrg,
    benchmarkCurveId: nextBenchmark,
    interpMethod: nextInterp,
  }) {
    if (
      requestedOrg?.id &&
      selectedOrg?.id &&
      String(requestedOrg.id) !== String(selectedOrg.id)
    ) {
      return false;
    }

    setSuppressDirty(true);
    try {
      setAdminLibraryEditEnabled(false);

      const hydrated = nextForm
        ? {
            ...DEFAULT_FORM,
            ...nextForm,

            dealClass:
              nextForm.dealClass ??
              nextForm.deal_class ??
              DEFAULT_FORM.dealClass,

            sharing: normalizeSharing(nextForm.sharing),
            status: nextForm.status ?? DEFAULT_FORM.status,

            issuerId: nextForm.issuerId ?? nextForm.issuer_id ?? null,
            issuerNameRaw: (
              nextForm.issuerNameRaw ??
              nextForm.issuer_name_raw ??
              ""
            ).toString(),
            issuerStatus:
              nextForm.issuerStatus ?? nextForm.issuer_status ?? "custom",

            dealType: normalizeDealPurpose(
              nextForm.dealType ??
              nextForm.deal_type ??
              nextForm.type ??
              DEFAULT_FORM.dealType
            ),

            financingType:
              nextForm.financingType ??
              nextForm.financing_type ??
              DEFAULT_FORM.financingType,
            
            lienPriority:
              nextForm.lienPriority ??
              nextForm.lien_priority ??
              DEFAULT_FORM.lienPriority,

            structureMode:
              nextForm.structureMode ??
              nextForm.structure_mode ??
              DEFAULT_FORM.structureMode,

            amortizationPeriods:
              nextForm.amortizationPeriods ??
              nextForm.amortization_periods ??
              DEFAULT_FORM.amortizationPeriods,

            loanTermPeriods:
              nextForm.loanTermPeriods ??
              nextForm.loan_term_periods ??
              DEFAULT_FORM.loanTermPeriods,

            targetType:
              nextForm.targetType ??
              nextForm.target_type ??
              DEFAULT_FORM.targetType,

            targetValue:
              nextForm.targetValue ??
              nextForm.target_value ??
              DEFAULT_FORM.targetValue,

            targetGross:
              nextForm.targetGross ??
              nextForm.target_gross ??
              DEFAULT_FORM.targetGross,

            fundingType:
              nextForm.fundingType ??
              nextForm.funding_type ??
              DEFAULT_FORM.fundingType,

            fiscalYearEnd: nextForm.fiscalYearEnd || "",
            firstCallDate:
              nextForm.firstCallDate ?? nextForm.first_call_date ?? "",
            firstCallDateToken: Number(nextForm.firstCallDateToken || 0),

            capiEnabled:
              nextForm.capiEnabled ??
              nextForm.capi_enabled ??
              DEFAULT_FORM.capiEnabled,

            capiDate:
              nextForm.capiDate ??
              nextForm.capi_date ??
              DEFAULT_FORM.capiDate ??
              "",

            capiInvestmentRate:
              nextForm.capiInvestmentRate ??
              nextForm.capi_investment_rate ??
              DEFAULT_FORM.capiInvestmentRate ??
              0,
          }
        : { ...DEFAULT_FORM };

      hydrated.additionalSources = normalizeAdditionalSources(
        nextForm?.additionalSources
      );

      const refundedIssuesRaw =
        nextForm?.refunded_issues ?? nextForm?.refundedIssues ?? [];

      const refundingUi = hydrateRefundingUiFromRefundedIssues(refundedIssuesRaw);

      hydrated.refundingMode = refundingUi.refundingMode;
      hydrated.refundingSolutionType = normalizeRefundingSolutionType(
        refundedIssuesRaw?.[0]?.solution_type || nextForm?.refundingSolutionType
      );
      hydrated.refundingManualDeposits = normalizeRefundingManualDeposits(
        refundingUi.refundingManualDeposits
      );

      const camelWrapManualCashflows = normalizeWrapManualCashflows(
        nextForm?.wrapManualCashflows
      );
      const snakeWrapManualCashflows = normalizeWrapManualCashflows(
        nextForm?.wrap_manual_cashflows
      );
      const configWrapManualCashflows = normalizeWrapManualCashflows(
        nextForm?.wrapConfig?.wrapPaymentRows ??
          nextForm?.wrap_config?.wrapPaymentRows
      );

      hydrated.wrapManualCashflows =
        camelWrapManualCashflows.length > 0
          ? camelWrapManualCashflows
          : snakeWrapManualCashflows.length > 0
          ? snakeWrapManualCashflows
          : configWrapManualCashflows;

      const camelWrapDsIssueRefs = normalizeWrapDsIssueRefs(
        nextForm?.wrapDsIssueRefs
      );
      const snakeWrapDsIssueRefs = normalizeWrapDsIssueRefs(
        nextForm?.wrap_ds_issue_refs
      );

      hydrated.wrapDsIssueRefs =
        camelWrapDsIssueRefs.length > 0
          ? camelWrapDsIssueRefs
          : snakeWrapDsIssueRefs;

      hydrated.wrapConfig = normalizeWrapConfig(
        nextForm?.wrapConfig ?? nextForm?.wrap_config,
        hydrated.wrapManualCashflows,
        hydrated.wrapDsIssueRefs
      );

      hydrated.capiEnabled = !!hydrated.capiEnabled;

      if (!hydrated.capiEnabled) {
        hydrated.capiDate = "";
        hydrated.capiInvestmentRate = 0;
      } else {
        hydrated.capiDate = hydrated.capiDate || "";
        hydrated.capiInvestmentRate =
          hydrated.capiInvestmentRate == null ||
          hydrated.capiInvestmentRate === ""
            ? 0
            : Number(hydrated.capiInvestmentRate);
      }

      hydrated.targetValue =
        hydrated.targetValue == null || String(hydrated.targetValue).trim() === ""
          ? null
          : Number(hydrated.targetValue);

      hydrated.targetGross =
        hydrated.targetGross == null || String(hydrated.targetGross).trim() === ""
          ? null
          : Number(hydrated.targetGross);

      hydrated.fundingType = (hydrated.fundingType ?? "").trim() || "gross";

      setForm(hydrated);
      setStructureRows(Array.isArray(nextRows) ? nextRows : []);
      setScale(nextScale !== undefined ? nextScale : null);
      setSolution(nextSolution !== undefined ? nextSolution : null);

      if (nextBenchmark) setBenchmarkCurveId(nextBenchmark);
      if (nextInterp) setInterpMethod(nextInterp);

      setLastSavedSnapshot({
        form: hydrated,
        structureRows: Array.isArray(nextRows) ? nextRows : [],
        scale: nextScale,
      });

      setStructureSignature(structureSignatureFromForm(hydrated));
      setFirstCallAppliedToken(Number(hydrated.firstCallDateToken || 0));
      _setIsDirty(false);

      try {
        const payload = {
          orgId: selectedOrg?.id ?? null,
          form: hydrated,
          structureRows: Array.isArray(nextRows) ? nextRows : [],
          scale: nextScale !== undefined ? nextScale : null,
          solution: nextSolution !== undefined ? nextSolution : null,
          selectedOrg,
          lastSavedSnapshot: {
            form: hydrated,
            structureRows: Array.isArray(nextRows) ? nextRows : [],
            scale: nextScale,
          },
          benchmarkCurveId: nextBenchmark || benchmarkCurveId,
          interpMethod: nextInterp || interpMethod,
          structureSignature: structureSignatureFromForm(hydrated),
          firstCallAppliedToken: Number(hydrated.firstCallDateToken || 0),
        };

        sessionStorage.setItem(SESSION_KEY, JSON.stringify(payload));
      } catch {
        // no-op
      }
    } finally {
      setTimeout(() => setSuppressDirty(false), 0);
    }

    return true;
  }

  return (
    <DebtStudioContext.Provider
      value={{
        form,
        currentOwner,
        setForm: updateForm,

        structureRows,
        setStructureRows: updateStructureRows,

        scale,
        setScale: updateScale,

        solution,
        setSolution,

        selectedOrg,

        hasActiveDeal,

        isOwner,
        canEdit,
        canDelete,
        canChangeSharing,
        isReadOnly,
        isLocked,
        isLibraryDeal,
        isPlatformAdmin,
        canEditLibraryDeal,
        adminLibraryEditEnabled,
        setAdminLibraryEditEnabled,

        resetAll,
        resetForm,

        ensureStructure,

        isDirty,
        setIsDirty,

        suppressDirty,

        runSilent,

        structureSignature,
        structureNeedsRebuild,
        markStructureSignatureCurrent,

        applyFirstCallDate,
        rebuildStructureFromTerms,

        saveDeal,
        loadDeal,

        benchmarkCurveId,
        setBenchmarkCurveId,

        interpMethod,
        setInterpMethod,

        lastSavedSnapshot,
        revertToLastSaved,
      }}
    >
      {children}
    </DebtStudioContext.Provider>
  );
}

/* ────────────────────────────────
   Validation helper
──────────────────────────────── */
export function validateDealForm(form) {
  const requiredFields = [
    "dealName",
    "financingType",
    "dealType",
    "interestMode",
    "taxStatus",
    "sharing",
    "datedDate",
    "deliveryDate",
    "firstCouponDate",
    "firstPrincipalDate",
    "finalMaturityDate",
    "denomination",
    "couponRate",
    "dayCount",
    "couponFrequency",
    "principalFrequency",
  ];

  const missing = requiredFields.filter((k) => !form[k]);
  return { ok: missing.length === 0, missing };
}

/* ────────────────────────────────
   Structure builder
──────────────────────────────── */
export function buildOrUpdateStructure(form, baseRows = []) {
  let rows = Array.isArray(baseRows) && baseRows.length > 0 ? [...baseRows] : [];

  const firstCall = form?.firstCallDate ? dayjs(form.firstCallDate) : null;
  const hasFirstCall = !!(firstCall && firstCall.isValid());
  const firstCallISO = hasFirstCall ? firstCall.format("YYYY-MM-DD") : null;

  if (rows.length === 0) {
    const firstPrin = dayjs(form.firstPrincipalDate);
    const finalMat = dayjs(form.finalMaturityDate);

    if (!firstPrin.isValid() || !finalMat.isValid()) return [];

    const freq = String(form.principalFrequency || "").toLowerCase();
    const monthsPerStep = freq.startsWith("semi")
      ? 6
      : freq.startsWith("quarter")
      ? 3
      : freq.startsWith("month")
      ? 1
      : 12;

    const appendRow = (date) => {
      const rowDate = date.format("YYYY-MM-DD");

      const defaultCallDate =
        hasFirstCall && dayjs(rowDate).isAfter(firstCall) ? firstCallISO : null;

      rows.push({
        date: rowDate,
        call_date: defaultCallDate,
        call_price: 100,
        principal: null,
        coupon: form.couponRate,
        yield: form.couponRate,
        price: null,
        bond_type: "serial",
        term_group: null,
      });
    };

    let cursor = firstPrin.clone();
    while (cursor.isBefore(finalMat) || cursor.isSame(finalMat)) {
      appendRow(cursor);

      cursor = cursor.add(monthsPerStep, "month");
    }

    // Final maturity is authoritative. If it falls between regular principal
    // dates, preserve it as a short final/stub period rather than stopping at
    // the preceding scheduled date.
    const finalMatISO = finalMat.format("YYYY-MM-DD");
    if (rows.length > 0 && rows[rows.length - 1].date !== finalMatISO) {
      appendRow(finalMat);
    }
  }

  rows = rows.map((r) => {
    const rowDate = r?.date ? dayjs(r.date) : null;
    const rowHasDate = !!(rowDate && rowDate.isValid());

    const existingCall = r?.call_date ? dayjs(r.call_date) : null;
    const hasExistingCall = !!(existingCall && existingCall.isValid());

    const defaultedCall =
      !hasExistingCall &&
      hasFirstCall &&
      rowHasDate &&
      rowDate.isAfter(firstCall)
        ? firstCallISO
        : r.call_date ?? null;

    return {
      ...r,
      call_date: defaultedCall,
      coupon: Number(r.coupon ?? form.couponRate),
      yield: Number(r.yield ?? form.couponRate),
      principal: r.principal != null ? Number(r.principal) : null,
    };
  });

  return recomputeDerivedFields(rows, {
    couponFrequency: form.couponFrequency,
    dayCount: form.dayCount,
    datedDate: form.datedDate,
    deliveryDate: form.deliveryDate,
  });
}

/* ────────────────────────────────
   Hook
──────────────────────────────── */
export function useDebtStudio() {
  const ctx = useContext(DebtStudioContext);
  if (!ctx) {
    throw new Error("useDebtStudio must be used within DebtStudioProvider");
  }
  return ctx;
}
